/**
 * SeoHead — renders all SEO meta tags into the document <head> via react-helmet-async.
 * Receives pre-resolved data from useSeoMeta hook.
 */

import { Helmet } from "react-helmet-async";
import type { ResolvedSeoMeta } from "@/types/seo";

interface SeoHeadProps {
  seo: ResolvedSeoMeta;
}

export function SeoHead({ seo }: SeoHeadProps) {
  return (
    <Helmet>
      <title>{seo.title}</title>
      {/* Empty descriptions are omitted, not rendered as content="": an empty tag
          tells search engines the page has no summary, while no tag lets them
          write a snippet from the page text. */}
      {seo.description && <meta name="description" content={seo.description} />}
      <meta name="robots" content={seo.robots} />

      {/* Canonical */}
      {seo.canonicalUrl && <link rel="canonical" href={seo.canonicalUrl} />}

      {/* Hreflang alternates */}
      {seo.alternates.map((alt) => (
        <link
          key={alt.hreflang}
          rel="alternate"
          hrefLang={alt.hreflang}
          href={alt.href}
        />
      ))}

      {/* Open Graph */}
      <meta property="og:title" content={seo.og.title} />
      {seo.og.description && (
        <meta property="og:description" content={seo.og.description} />
      )}
      <meta property="og:type" content={seo.og.type} />
      <meta property="og:url" content={seo.og.url} />
      <meta property="og:locale" content={seo.og.locale} />
      <meta property="og:site_name" content={seo.og.siteName} />
      {seo.og.image && <meta property="og:image" content={seo.og.image} />}
      {seo.og.imageWidth && (
        <meta property="og:image:width" content={String(seo.og.imageWidth)} />
      )}
      {seo.og.imageHeight && (
        <meta property="og:image:height" content={String(seo.og.imageHeight)} />
      )}
      {seo.og.imageAlt && (
        <meta property="og:image:alt" content={seo.og.imageAlt} />
      )}

      {/* Twitter Card */}
      <meta name="twitter:card" content={seo.twitter.cardType} />
      <meta name="twitter:title" content={seo.twitter.title} />
      {seo.twitter.description && (
        <meta name="twitter:description" content={seo.twitter.description} />
      )}
      {seo.twitter.image && (
        <meta name="twitter:image" content={seo.twitter.image} />
      )}

      {/* Article meta */}
      {seo.article?.publishedTime && (
        <meta
          property="article:published_time"
          content={seo.article.publishedTime}
        />
      )}

      {/* Product meta */}
      {seo.product && (
        <meta
          property="product:price:amount"
          content={seo.product.priceAmount}
        />
      )}
      {seo.product && (
        <meta
          property="product:price:currency"
          content={seo.product.priceCurrency}
        />
      )}
    </Helmet>
  );
}
